import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Send, LayoutTemplate, PenSquare, Tag, Shield, Activity, Zap, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSetAtom } from "jotai";
import { campaignWizardAtom } from "@/lib/atoms";
import { track } from "@/lib/analytics";
import { useEmailComposer } from "@/components/email/EmailComposerProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useToast } from "@/hooks/use-toast";
import { captureError } from '@/lib/sentry';

interface EmailSidebarProps {
  activePage: string;
  onPageChange: (page: string) => void;
}

interface EmailNavItem {
  title: string;
  id: string;
  icon: any;
  badge?: string;
  adminOnly?: boolean;
}

const emailNavItems: EmailNavItem[] = [
  {
    title: "Campaigns",
    id: "campaigns",
    icon: Zap,
  },
  {
    title: "Activity",
    id: "activity",
    icon: Activity,
  },
  {
    title: "Sent",
    id: "sent",
    icon: Send,
  },
  {
    title: "Templates",
    id: "templates",
    icon: LayoutTemplate,
  },
  {
    title: "Admin",
    id: "admin",
    icon: Shield,
    adminOnly: true,
  },
];

export function EmailSidebar({ activePage, onPageChange }: EmailSidebarProps) {
  const { openComposer } = useEmailComposer();
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const [tags, setTags] = useState<{ id: string; name: string; count: number }[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTagId = searchParams.get('tag');
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isAdmin = currentWorkspace?.role === 'owner' || currentWorkspace?.role === 'admin';
  const visibleNavItems = emailNavItems.filter((item) => !item.adminOnly || isAdmin);

  const handlePageChange = (page: string) => {
    // Map pages to their corresponding routes
    const pageRoutes: Record<string, string> = {
      campaigns: '/inbox?tab=campaigns',
      templates: '/inbox?tab=templates',
      activity: '/inbox?tab=activity',
      sent: '/inbox?tab=sent',
      admin: '/inbox?tab=admin'
    }
    
    // Navigate to the appropriate route
    navigate(pageRoutes[page] || '/inbox');
    onPageChange(page);
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchTags();
    }
  }, [currentWorkspace?.id]);

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from('template_tag_relations')
        .select(`
          template_tags (
            id,
            name
          ),
          template_id
        `)
        .eq('template_tags.workspace_id', currentWorkspace?.id);

      if (error) throw error;

      const tagCounts = data.reduce((acc, curr) => {
        const tags = Array.isArray(curr.template_tags) ? curr.template_tags : [curr.template_tags];
        tags.forEach(tag => {
          if (!tag) return;
          
          if (!acc[tag.id]) {
            acc[tag.id] = {
              id: tag.id,
              name: tag.name,
              count: 0
            };
          }
          acc[tag.id].count++;
        });
        return acc;
      }, {} as Record<string, { id: string; name: string; count: number }>);

      setTags(Object.values(tagCounts));
    } catch (error) {
      console.error('Error fetching tags:', error);
      captureError(error as Error, { feature: 'email-sidebar-fetch-tags' });
      toast({
        title: "Error",
        description: "Failed to load template tags",
        variant: "destructive",
      });
    }
  };

  // Separate component for tag section to improve readability
  const TagSection = () => {
    if (activePage !== 'templates') return null;

    return (
      <div className="mt-2 space-y-1">
        <div className="px-2 py-1 text-sm font-medium text-muted-foreground">
          Tags ({tags.length})
        </div>
        <div className="space-y-1">
          {tags.map(tag => (
            <Button
              key={tag.id}
              variant="ghost"
              className={cn(
                "w-full justify-start pl-8",
                selectedTagId === tag.id && "bg-accent"
              )}
              onClick={() => {
                setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', 'templates');
                  if (next.get('tag') === tag.id) {
                    next.delete('tag');
                  } else {
                    next.set('tag', tag.id);
                  }
                  return next;
                });
              }}
            >
              <Tag className="h-4 w-4 mr-2" />
              {tag.name}
              <span className="ml-auto text-xs text-muted-foreground">
                {tag.count}
              </span>
            </Button>
          ))}
          {tags.length === 0 && (
            <div className="px-2 py-1 text-sm text-muted-foreground">
              No tags yet
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="py-2 flex flex-col h-full">
      <div className="px-4 mb-6">
        {/* Split button: the primary action opens the campaign wizard (what most
            users want when they click the Email section's main button); the caret
            reveals the secondary one-off email composer. Replaces the old "Compose"
            button, which opened the one-off composer and confused campaign-seekers. */}
        <div className="flex w-full">
          <Button
            className="flex-1 justify-start rounded-r-none"
            onClick={() => {
              track('campaign_wizard_opened', { source: 'email_sidebar', workspace_id: currentWorkspace?.id });
              setCampaignWizard({ open: true, selectedContacts: [] });
            }}
          >
            <Zap className="mr-2 h-4 w-4" />
            Create Campaign
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="rounded-l-none border-l border-primary-foreground/20 px-2"
                aria-label="More create options"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  track('oneoff_composer_opened', { source: 'email_sidebar', workspace_id: currentWorkspace?.id });
                  openComposer();
                }}
              >
                <PenSquare className="mr-2 h-4 w-4" />
                One-Off Email
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <nav className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant="ghost"
                className={cn( 
                  "w-full justify-between",
                  activePage === item.id && "bg-accent"
                )}
                onClick={() => handlePageChange(item.id)}
              >
                <div className="flex items-center">
                  <Icon className="mr-2 h-4 w-4" />
                  {item.title}
                </div>
                {item.badge && (
                  <Badge variant="secondary" className="text-xs px-1">
                    {item.badge}
                  </Badge>
                )}
              </Button>
            );
          })}
        </nav>
        
        <TagSection />
      </ScrollArea>
    </div>
  );
}